/**
 * src/lib/yard-data.ts
 *
 * Live Supabase store. Same subscribe/getSnapshot interface the screens
 * already use with useSyncExternalStore — this file is the only thing that
 * swaps.
 *
 * Scoping note: every query filters by yard_id for relevance/UX, but the
 * actual SECURITY boundary is RLS. A user cannot receive rows — via query or
 * Realtime — outside their org's access regardless of these filters.
 *
 * Schema wrinkle: containers, agent_events, staging_spots, sequence_queue and
 * notifications carry yard_id directly, so they filter server-side and at the
 * Realtime channel level. assignments does NOT (it scopes via
 * container_id -> containers.yard_id), so its initial fetch uses a join and
 * its channel has no server-side filter; RLS still fully protects it.
 */

import { supabase } from "@/integrations/supabase/client";
import type { RealtimeChannel } from "@supabase/supabase-js";

/* ---------- UI-facing types (what the screens render) ---------- */

export type ContainerStatus =
  | "expected"
  | "gate_in"
  | "staged"
  | "in_process"
  | "ready_for_pickup"
  | "gate_out"
  | "exception";

export type AgentName = "sense" | "sequence" | "assign" | "resolve" | "learn";
export type NotificationSeverity = "info" | "warning" | "critical";
export type LoopStage = "SENSE" | "SEQUENCE" | "ASSIGN" | "RESOLVE" | "LEARN";
export type TrailerType = "Dry Van 53'" | "Reefer 40'" | "Chassis 20'" | "Flatbed" | "Tanker";

export interface TruckRow {
  id: string;
  queueNo: number;
  carrier: string;
  driver: string;
  container: string;
  trailerType: TrailerType;
  status: ContainerStatus;
  dock: string | null;
  spot: string | null;
  arrivedAt: number;
  appointmentAt: number;
  checkInMethod: "QR" | "SMS";
}

export interface AgentEvent {
  id: string;
  stage: LoopStage;
  at: number;
  message: string;
}

export interface Spot {
  id: string;
  label: string;
  kind: "dock" | "staging";
  occupantId: string | null;
}

export interface DisputeTimelineEntry {
  label: string;
  at: number;
  stage: LoopStage;
  source: string;
}

export interface Dispute {
  id: string;
  container: string;
  carrier: string;
  type: "detention" | "damage" | "mis-assignment" | "billing" | "other";
  status: "open" | "under_review" | "resolved" | "escalated";
  amount: number;
  openedAt: number;
  summary: string;
  timeline: DisputeTimelineEntry[];
}

export interface Notification {
  id: string;
  severity: NotificationSeverity;
  message: string;
  isRead: boolean;
  at: number;
}

export interface YardSettings {
  yardName: string;
  address: string;
  timezone: string;
  freeTimeHours: number;
}

export interface YardSnapshot {
  yardId: string | null;
  loading: boolean;
  error: string | null;
  trucks: TruckRow[];
  spots: Spot[];
  events: AgentEvent[];
  disputes: Dispute[];
  notifications: Notification[];
  settings: YardSettings;
}

/* ---------- raw row shapes (mirror the live tables) ---------- */

interface ContainerRow {
  id: string;
  yard_id: string;
  container_number: string;
  chassis_number: string | null;
  carrier_id: string | null;
  status: ContainerStatus;
  current_spot_id: string | null;
  created_at: string;
}

interface AgentEventRow {
  id: string;
  yard_id: string;
  container_id: string | null;
  agent: AgentName;
  summary: string;
  created_at: string;
}

interface SpotRow {
  id: string;
  yard_id: string;
  label: string;
  spot_type: string;
  is_occupied: boolean;
}

interface QueueRow {
  id: string;
  yard_id: string;
  container_id: string;
  queue_position: number;
}

interface NotificationRow {
  id: string;
  yard_id: string;
  severity: NotificationSeverity;
  message: string;
  is_read: boolean;
  created_at: string;
}

interface AssignmentRow {
  id: string;
  container_id: string;
  spot_id: string;
  assigned_by_agent: boolean;
  assigned_at: string;
  released_at: string | null;
}

interface DisputeRow {
  id: string;
  yard_id: string;
  container_id: string | null;
  dispute_type: string;
  status: Dispute["status"];
  description: string | null;
  resolution: string | null;
  opened_at: string;
}

interface Raw {
  containers: ContainerRow[];
  agentEvents: AgentEventRow[];
  spots: SpotRow[];
  queue: QueueRow[];
  notifications: NotificationRow[];
  assignments: AssignmentRow[];
  disputes: DisputeRow[];
  carriers: Map<string, string>;
  drivers: Map<string, string>;
  appointments: Map<string, { at: number; driverId: string | null }>;
  gateIn: Map<string, number>;
}

const MIN = 60_000;

export const STATUS_LABEL: Record<ContainerStatus, string> = {
  expected: "Expected",
  gate_in: "Checked In",
  staged: "Staged",
  in_process: "In Process",
  ready_for_pickup: "Ready for Pickup",
  gate_out: "Departed",
  exception: "Exception",
};

const AGENT_STAGE: Record<string, LoopStage> = {
  sense: "SENSE",
  sequence: "SEQUENCE",
  assign: "ASSIGN",
  resolve: "RESOLVE",
  learn: "LEARN",
};

const DISPUTE_TYPE: Record<string, Dispute["type"]> = {
  detention: "detention",
  damage: "damage",
  mis_assignment: "mis-assignment",
  billing: "billing",
  other: "other",
};

const emptyRaw = (): Raw => ({
  containers: [],
  agentEvents: [],
  spots: [],
  queue: [],
  notifications: [],
  assignments: [],
  disputes: [],
  carriers: new Map(),
  drivers: new Map(),
  appointments: new Map(),
  gateIn: new Map(),
});

const EMPTY_SNAPSHOT: YardSnapshot = {
  yardId: null,
  loading: true,
  error: null,
  trucks: [],
  spots: [],
  events: [],
  disputes: [],
  notifications: [],
  settings: { yardName: "", address: "", timezone: "America/Chicago", freeTimeHours: 48 },
};

let raw = emptyRaw();
let snapshot: YardSnapshot = EMPTY_SNAPSHOT;
let activeYardId: string | null = null;
let channels: RealtimeChannel[] = [];
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((cb) => cb());
const ts = (v: string | null | undefined) => (v ? new Date(v).getTime() : 0);

function upsertById<T extends { id: string }>(list: T[], row: T): T[] {
  const idx = list.findIndex((r) => r.id === row.id);
  if (idx === -1) return [row, ...list];
  const copy = list.slice();
  copy[idx] = row;
  return copy;
}

function removeById<T extends { id: string }>(list: T[], id: string): T[] {
  return list.filter((r) => r.id !== id);
}

/* ---------- derivation: raw rows -> screen shapes ---------- */

function derive(patch: Partial<YardSnapshot> = {}) {
  const spotById = new Map(raw.spots.map((s) => [s.id, s]));
  const queueByContainer = new Map(raw.queue.map((q) => [q.container_id, q.queue_position]));

  const trucks: TruckRow[] = raw.containers.map((c, i) => {
    const spot = c.current_spot_id ? spotById.get(c.current_spot_id) : undefined;
    const isDock = spot?.spot_type === "dock";
    const appt = raw.appointments.get(c.id);
    return {
      id: c.id,
      queueNo: queueByContainer.get(c.id) ?? 101 + i,
      carrier: (c.carrier_id ? raw.carriers.get(c.carrier_id) : undefined) ?? "Unassigned carrier",
      driver: (appt?.driverId ? raw.drivers.get(appt.driverId) : undefined) ?? "—",
      container: c.container_number,
      trailerType: (c.chassis_number ? "Chassis 20'" : "Dry Van 53'") as TrailerType,
      status: c.status,
      dock: isDock ? (spot?.label ?? null) : null,
      spot: spot && !isDock ? spot.label : null,
      arrivedAt: raw.gateIn.get(c.id) ?? ts(c.created_at),
      appointmentAt: appt?.at ?? ts(c.created_at),
      checkInMethod: "QR" as const,
    };
  });

  const occupantBySpot = new Map(
    raw.containers.filter((c) => c.current_spot_id).map((c) => [c.current_spot_id!, c.id]),
  );

  const spots: Spot[] = raw.spots.map((s) => ({
    id: s.id,
    label: s.label,
    kind: s.spot_type === "dock" ? "dock" : "staging",
    occupantId: occupantBySpot.get(s.id) ?? null,
  }));

  const events: AgentEvent[] = raw.agentEvents.map((e) => ({
    id: e.id,
    stage: AGENT_STAGE[e.agent] ?? "SENSE",
    at: ts(e.created_at),
    message: e.summary,
  }));

  const containerById = new Map(raw.containers.map((c) => [c.id, c]));

  const disputes: Dispute[] = raw.disputes.map((d) => {
    const c = d.container_id ? containerById.get(d.container_id) : undefined;
    return {
      id: d.id,
      container: c?.container_number ?? "—",
      carrier: (c?.carrier_id ? raw.carriers.get(c.carrier_id) : undefined) ?? "Unassigned carrier",
      type: DISPUTE_TYPE[d.dispute_type] ?? "billing",
      status: d.status,
      amount: 0,
      openedAt: ts(d.opened_at),
      summary: d.resolution ?? d.description ?? "",
      timeline: raw.agentEvents
        .filter((e) => e.container_id && e.container_id === d.container_id)
        .map((e) => ({
          label: e.summary,
          at: ts(e.created_at),
          stage: AGENT_STAGE[e.agent] ?? "SENSE",
          source: "agent_events",
        }))
        .sort((a, b) => a.at - b.at),
    };
  });

  const notifications: Notification[] = raw.notifications.map((n) => ({
    id: n.id,
    severity: n.severity,
    message: n.message,
    isRead: n.is_read,
    at: ts(n.created_at),
  }));

  snapshot = { ...snapshot, trucks, spots, events, disputes, notifications, ...patch };
  notify();
}

function setSnapshot(patch: Partial<YardSnapshot>) {
  snapshot = { ...snapshot, ...patch };
  notify();
}

/* ---------- public: subscribe / getSnapshot ---------- */

export function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  void bootstrap();
  return () => {
    listeners.delete(callback);
  };
}

export function getSnapshot(): YardSnapshot {
  return snapshot;
}

/* ---------- lifecycle ---------- */

let bootstrapped = false;

/**
 * Resolves the first yard this user can read, then activates it.
 *
 * Waits for the Supabase session before querying: an anonymous read returns
 * zero rows under RLS, and latching that result would leave the whole app on
 * fallback settings for the rest of the session.
 */
async function bootstrap(force = false) {
  if (typeof window === "undefined") return;
  if (bootstrapped && !force) return;
  bootstrapped = true;

  const { data: userRes } = await supabase.auth.getUser();
  if (!userRes.user) {
    bootstrapped = false; // retry once the user actually signs in
    return setSnapshot({ loading: false });
  }

  const { data, error } = await supabase
    .from("yards")
    .select("id")
    .order("created_at", { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error) {
    bootstrapped = false;
    return setSnapshot({ loading: false, error: error.message });
  }
  if (!data) return setSnapshot({ loading: false });
  await setActiveYard(data.id);
}

/**
 * Truth check used by the auth layer: does a real yard row exist that this
 * signed-in user can read? RLS scopes this to the user's org, so a hit means
 * onboarding genuinely completed — no localStorage flag involved.
 */
export async function fetchUserYardId(): Promise<string | null> {
  const { data, error } = await supabase
    .from("yards")
    .select("id")
    .order("created_at", { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data?.id ?? null;
}

/**
 * Creates the yard for the signed-in user's organization and activates it.
 * Throws when the org can't be resolved or the insert is rejected, so callers
 * can refuse to navigate on failure.
 */
export async function createYard(input: YardSettings): Promise<string> {
  const { data: userRes } = await supabase.auth.getUser();
  const user = userRes.user;
  if (!user) throw new Error("You are signed out. Sign in again to set up your yard.");

  // Idempotency guard: a double submit (or a second tab) must not create a
  // second yard for the same org. If one already exists, adopt it.
  const existing = await fetchUserYardId();
  if (existing) {
    bootstrapped = true;
    await setActiveYard(existing);
    return existing;
  }



  const { data: member, error: memberErr } = await supabase
    .from("org_members")
    .select("org_id")
    .eq("user_id", user.id)
    .order("created_at", { ascending: true })
    .limit(1)
    .maybeSingle();
  if (memberErr) throw new Error(memberErr.message);
  if (!member?.org_id) throw new Error("No organization found for this account.");

  const { data, error } = await supabase
    .from("yards")
    .insert({
      org_id: member.org_id,
      name: input.yardName,
      address: input.address,
      timezone: input.timezone,
      dwell_target_hours: input.freeTimeHours,
    })
    .select("id")
    .single();
  if (error) throw new Error(error.message);
  if (!data?.id) throw new Error("Yard insert returned no row.");

  bootstrapped = true;
  await setActiveYard(data.id);
  return data.id;
}

/**
 * Call once a yard is known. Safe to call again when the active yard changes —
 * tears down the previous yard's subscriptions first.
 */
export async function setActiveYard(yardId: string): Promise<void> {
  if (activeYardId === yardId) return;
  teardownChannels();
  activeYardId = yardId;
  raw = emptyRaw();
  snapshot = { ...EMPTY_SNAPSHOT, yardId, loading: true };
  notify();

  try {
    await fetchInitial(yardId);
    derive({ loading: false });
    subscribeRealtime(yardId);
  } catch (err) {
    setSnapshot({ loading: false, error: err instanceof Error ? err.message : "Failed to load yard data" });
  }
}

/** Call on sign-out — clears cache and channels so the next session never sees stale rows. */
export function resetStore(): void {
  teardownChannels();
  activeYardId = null;
  bootstrapped = false;
  raw = emptyRaw();
  snapshot = EMPTY_SNAPSHOT;
  notify();
}

function teardownChannels(): void {
  channels.forEach((ch) => void supabase.removeChannel(ch));
  channels = [];
}

/* ---------- initial fetch ---------- */

async function fetchInitial(yardId: string): Promise<void> {
  const [yard, containers, agentEvents, spots, queue, notifications, assignments, disputes, carriers, gateEvents] =
    await Promise.all([
      supabase.from("yards").select("name, address, timezone, dwell_target_hours").eq("id", yardId).maybeSingle(),
      supabase.from("containers").select("*").eq("yard_id", yardId).order("created_at", { ascending: false }),
      supabase.from("agent_events").select("*").eq("yard_id", yardId).order("created_at", { ascending: false }).limit(200),
      supabase.from("staging_spots").select("*").eq("yard_id", yardId).order("label"),
      supabase.from("sequence_queue").select("*").eq("yard_id", yardId).order("queue_position", { ascending: true }),
      supabase.from("notifications").select("*").eq("yard_id", yardId).order("created_at", { ascending: false }).limit(50),
      // assignments has no yard_id column — filter via the containers join instead
      supabase.from("assignments").select("*, containers!inner(yard_id)").eq("containers.yard_id", yardId),
      supabase.from("disputes").select("*").eq("yard_id", yardId).order("opened_at", { ascending: false }),
      supabase.from("carriers").select("id, name"),
      supabase.from("gate_events").select("container_id, event_type, occurred_at").order("occurred_at"),
    ]);

  for (const [name, res] of Object.entries({
    yard, containers, agentEvents, spots, queue, notifications, assignments, disputes, carriers, gateEvents,
  })) {
    if (res.error) throw new Error(`${name} fetch failed: ${res.error.message}`);
  }

  const containerIds = (containers.data ?? []).map((c) => c.id);
  const appts = containerIds.length
    ? await supabase.from("appointments").select("container_id, scheduled_at, driver_id").in("container_id", containerIds)
    : { data: [], error: null };
  if (appts.error) throw new Error(`appointments fetch failed: ${appts.error.message}`);

  const driverIds = [...new Set((appts.data ?? []).map((a) => a.driver_id).filter((id): id is string => !!id))];
  const drivers = driverIds.length
    ? await supabase.from("drivers").select("id, name").in("id", driverIds)
    : { data: [], error: null };
  if (drivers.error) throw new Error(`drivers fetch failed: ${drivers.error.message}`);

  raw = {
    containers: (containers.data ?? []) as ContainerRow[],
    agentEvents: (agentEvents.data ?? []) as AgentEventRow[],
    spots: (spots.data ?? []) as SpotRow[],
    queue: (queue.data ?? []) as QueueRow[],
    notifications: (notifications.data ?? []) as NotificationRow[],
    assignments: (assignments.data ?? []).map(({ containers: _drop, ...rest }) => rest) as AssignmentRow[],
    disputes: (disputes.data ?? []) as DisputeRow[],
    carriers: new Map((carriers.data ?? []).map((c) => [c.id, c.name])),
    drivers: new Map((drivers.data ?? []).map((d) => [d.id, d.name])),
    appointments: new Map(
      (appts.data ?? []).map((a) => [a.container_id, { at: ts(a.scheduled_at), driverId: a.driver_id }]),
    ),
    gateIn: new Map(
      (gateEvents.data ?? [])
        .filter((g) => g.event_type === "in" || g.event_type === "gate_in")
        .map((g) => [g.container_id, ts(g.occurred_at)]),
    ),
  };

  const y = yard.data;
  snapshot = {
    ...snapshot,
    settings: {
      yardName: y?.name ?? "",
      address: y?.address ?? "",
      timezone: y?.timezone ?? "America/Chicago",
      freeTimeHours: Number(y?.dwell_target_hours ?? 48),
    },
  };
}

/* ---------- realtime ---------- */

function subscribeRealtime(yardId: string): void {
  const filter = `yard_id=eq.${yardId}`;

  const containersCh = supabase
    .channel(`containers:${yardId}`)
    .on("postgres_changes", { event: "*", schema: "public", table: "containers", filter }, (payload) => {
      raw.containers =
        payload.eventType === "DELETE"
          ? removeById(raw.containers, (payload.old as ContainerRow).id)
          : upsertById(raw.containers, payload.new as ContainerRow);
      derive();
    })
    .subscribe();

  const agentEventsCh = supabase
    .channel(`agent_events:${yardId}`)
    .on("postgres_changes", { event: "INSERT", schema: "public", table: "agent_events", filter }, (payload) => {
      // agent_events is insert-only — only INSERT is ever expected
      raw.agentEvents = [payload.new as AgentEventRow, ...raw.agentEvents].slice(0, 200);
      derive();
    })
    .subscribe();

  const spotsCh = supabase
    .channel(`staging_spots:${yardId}`)
    .on("postgres_changes", { event: "*", schema: "public", table: "staging_spots", filter }, (payload) => {
      raw.spots =
        payload.eventType === "DELETE"
          ? removeById(raw.spots, (payload.old as SpotRow).id)
          : upsertById(raw.spots, payload.new as SpotRow);
      derive();
    })
    .subscribe();

  const queueCh = supabase
    .channel(`sequence_queue:${yardId}`)
    .on("postgres_changes", { event: "*", schema: "public", table: "sequence_queue", filter }, (payload) => {
      raw.queue =
        payload.eventType === "DELETE"
          ? removeById(raw.queue, (payload.old as QueueRow).id)
          : upsertById(raw.queue, payload.new as QueueRow);
      derive();
    })
    .subscribe();

  const notificationsCh = supabase
    .channel(`notifications:${yardId}`)
    .on("postgres_changes", { event: "*", schema: "public", table: "notifications", filter }, (payload) => {
      raw.notifications =
        payload.eventType === "DELETE"
          ? removeById(raw.notifications, (payload.old as NotificationRow).id)
          : upsertById(raw.notifications, payload.new as NotificationRow);
      derive();
    })
    .subscribe();

  // No yard_id filter available server-side — RLS still fully scopes what this
  // client can receive; it just can't be narrowed at the channel level.
  const assignmentsCh = supabase
    .channel(`assignments:${yardId}`)
    .on("postgres_changes", { event: "*", schema: "public", table: "assignments" }, (payload) => {
      raw.assignments =
        payload.eventType === "DELETE"
          ? removeById(raw.assignments, (payload.old as AssignmentRow).id)
          : upsertById(raw.assignments, payload.new as AssignmentRow);
      derive();
    })
    .subscribe();

  const disputesCh = supabase
    .channel(`disputes:${yardId}`)
    .on("postgres_changes", { event: "*", schema: "public", table: "disputes", filter }, (payload) => {
      raw.disputes =
        payload.eventType === "DELETE"
          ? removeById(raw.disputes, (payload.old as DisputeRow).id)
          : upsertById(raw.disputes, payload.new as DisputeRow);
      derive();
    })
    .subscribe();

  channels = [containersCh, agentEventsCh, spotsCh, queueCh, notificationsCh, assignmentsCh, disputesCh];
}

/* ---------- auth lifecycle ---------- */

if (typeof window !== "undefined") {
  supabase.auth.onAuthStateChange((event: string) => {
    if (event === "SIGNED_OUT") resetStore();
    if (event === "SIGNED_IN" || event === "INITIAL_SESSION") void bootstrap(true);
  });
}

/* ---------- writes ---------- */

export async function updateSettings(patch: Partial<YardSettings>) {
  setSnapshot({ settings: { ...snapshot.settings, ...patch } });
  if (!activeYardId) return;
  await supabase
    .from("yards")
    .update({
      ...(patch.yardName !== undefined ? { name: patch.yardName } : {}),
      ...(patch.address !== undefined ? { address: patch.address } : {}),
      ...(patch.timezone !== undefined ? { timezone: patch.timezone } : {}),
      ...(patch.freeTimeHours !== undefined ? { dwell_target_hours: patch.freeTimeHours } : {}),
    })
    .eq("id", activeYardId);
}

export function setSpots(spots: Spot[]) {
  setSnapshot({ spots });
}

export async function addSpot(label: string, kind: Spot["kind"]) {
  if (!activeYardId) return;
  const { data } = await supabase
    .from("staging_spots")
    .insert({ yard_id: activeYardId, label, spot_type: kind })
    .select("*")
    .maybeSingle();
  if (data) {
    raw.spots = upsertById(raw.spots, data as SpotRow);
    derive();
  }
}

export async function removeSpot(id: string) {
  raw.spots = removeById(raw.spots, id);
  derive();
  await supabase.from("staging_spots").delete().eq("id", id);
}

/* ---------- derived helpers ---------- */

export const ON_SITE: ContainerStatus[] = ["gate_in", "staged", "in_process", "ready_for_pickup", "exception"];

export function waitMinutes(t: TruckRow, now: number) {
  return Math.max(0, Math.round((now - t.arrivedAt) / MIN));
}

export function fmtDuration(mins: number) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m.toString().padStart(2, "0")}m` : `${m}m`;
}

export function fmtClock(value: number) {
  return new Date(value).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
}

export function fmtDate(value: number) {
  return new Date(value).toLocaleDateString("en-US", { month: "short", day: "2-digit" });
}

export function sequenceScore(t: TruckRow, now: number) {
  const arrivalWeight = Math.min(60, waitMinutes(t, now)) * 0.8;
  const apptMinutes = (t.appointmentAt - now) / MIN;
  const apptWeight = apptMinutes < 0 ? 55 : Math.max(0, 45 - apptMinutes * 0.5);
  const trailerWeight = t.trailerType.startsWith("Reefer") ? 18 : t.trailerType.startsWith("Tanker") ? 12 : 6;
  return {
    arrivalWeight: Math.round(arrivalWeight),
    apptWeight: Math.round(apptWeight),
    trailerWeight,
    total: Math.round(arrivalWeight + apptWeight + trailerWeight),
    apptMinutes: Math.round(apptMinutes),
  };
}

/* ---------- analytics (LEARN) ---------- */

export function throughputSeries() {
  const hours = ["06", "07", "08", "09", "10", "11", "12", "13", "14", "15", "16", "17", "18"];
  const buckets = new Map(hours.map((h) => [h, { trucks: 0, dwellTotal: 0, dwellCount: 0 }]));
  for (const t of snapshot.trucks) {
    const h = new Date(t.arrivedAt).getHours().toString().padStart(2, "0");
    const b = buckets.get(h);
    if (!b) continue;
    b.trucks += 1;
    b.dwellTotal += Math.round((Date.now() - t.arrivedAt) / MIN);
    b.dwellCount += 1;
  }
  return hours.map((hour) => {
    const b = buckets.get(hour)!;
    return { hour, trucks: b.trucks, dwell: b.dwellCount ? Math.round(b.dwellTotal / b.dwellCount) : 0 };
  });
}

export function dwellTrend() {
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const target = snapshot.settings.freeTimeHours * 60;
  const buckets = new Map(days.map((d) => [d, { total: 0, count: 0, over: 0 }]));
  for (const t of snapshot.trucks) {
    const b = buckets.get(days[new Date(t.arrivedAt).getDay()]!)!;
    const mins = Math.round((Date.now() - t.arrivedAt) / MIN);
    b.total += mins;
    b.count += 1;
    if (mins > target) b.over += 1;
  }
  return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => {
    const b = buckets.get(day)!;
    return {
      day,
      dwell: b.count ? Math.round(b.total / b.count) : 0,
      overPct: b.count ? Math.round((b.over / b.count) * 100) : 0,
    };
  });
}
